import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { AppProviders } from '@/app/providers/AppProviders';
import { applyStoredTheme } from '@/app/providers/ThemeProvider';
import { AppRoutes } from '@/app/routes/AppRoutes';
import { RuntimeConfigError, loadRuntimeConfig } from '@/lib/config/runtime-config';
import { createQueryClient } from '@/lib/query/query-client';
import '@/styles/global.css';

/**
 * Entry point.
 *
 * Boot order matters:
 *   1. Apply the stored theme synchronously, so the first paint is the right colour.
 *   2. AWAIT the runtime config, so no request can be issued against an unresolved
 *      base URL. The legacy client started rendering — and fetching — while its
 *      config request was still in flight.
 *   3. Only then mount React.
 */

function mountFatal(message: string): void {
  const root = document.getElementById('root');
  if (!root) return;
  root.innerHTML = '';

  const container = document.createElement('div');
  container.setAttribute('role', 'alert');
  container.style.cssText =
    'min-height:100vh;display:grid;place-items:center;padding:24px;text-align:center;font-family:system-ui,sans-serif;';

  const heading = document.createElement('h1');
  heading.textContent = 'This app is not configured correctly';
  heading.style.cssText = 'font-size:20px;margin-bottom:8px;';

  const detail = document.createElement('p');
  detail.textContent = message;
  detail.style.cssText = 'font-size:14px;color:#5e5e72;max-width:480px;';

  container.append(heading, detail);
  root.append(container);
}

async function bootstrap(): Promise<void> {
  applyStoredTheme();

  try {
    await loadRuntimeConfig();
  } catch (error) {
    // A misconfigured deploy fails loudly here rather than producing an app that
    // silently talks to the wrong backend.
    mountFatal(
      error instanceof RuntimeConfigError
        ? error.message
        : 'Could not load runtime configuration.',
    );
    return;
  }

  const container = document.getElementById('root');
  if (!container) throw new Error('#root is missing from index.html');

  createRoot(container).render(
    <StrictMode>
      <BrowserRouter>
        <AppProviders queryClient={createQueryClient()}>
          <AppRoutes />
        </AppProviders>
      </BrowserRouter>
    </StrictMode>,
  );
}

void bootstrap();
